import type { HttpClient } from './client/http';
import type { KvResponse, KvListResponse, BulkReadResponse } from './client/types';
import { SdkError, SdkValidationError } from './client/errors';
import { AppDataCache } from './appDataCache';
import { KEY_RE } from './constants';
import { broadcastAppDataChange } from './appDataSubscribe';

function assertValidKey(key: string): void {
  if (typeof key !== 'string' || !KEY_RE.test(key)) {
    throw new SdkValidationError(
      `Invalid appData key "${key}". Must match /^[a-z0-9][a-z0-9_.-]{0,127}$/ (snake_case, lowercase). Got something with uppercase or non-allowed chars — rename to snake_case.`,
      'app_data.invalid_key',
      400,
    );
  }
}

export interface AppDataMethods {
  /**
   * Retorna `null` se a key não existir.
   *
   * G49: backend retorna `200 { value: null, updated_at: null }` pra chave
   * nunca escrita. SDKs < 0.5.0 viam 404; o shim no get() abaixo continua
   * cobrindo essa janela de skew de release até backend novo estar live por
   * >= 2 semanas (a remover em SDK 0.7.0).
   *
   * G50: consulta o cache primeado por `bulkRead` transparentemente; se a key
   * estiver no cache, evita o round-trip de rede.
   *
   * @throws SdkValidationError se a key não bater /^[a-z0-9][a-z0-9_.-]{0,127}$/.
   * @throws SdkError on network/5xx (retried 3x).
   */
  get(key: string): Promise<KvResponse | null>;
  /**
   * @throws SdkValidationError se a key não bater /^[a-z0-9][a-z0-9_.-]{0,127}$/.
   * @throws SdkError on network/5xx (NOT retried — fail-fast).
   * @idempotent (UPSERT)
   */
  set(key: string, value: unknown): Promise<KvResponse>;
  /**
   * @throws SdkValidationError se a key não bater /^[a-z0-9][a-z0-9_.-]{0,127}$/.
   * @throws SdkError on network/5xx (NOT retried — fail-fast).
   * @idempotent
   */
  delete(key: string): Promise<void>;
  /** @throws SdkError on network/5xx (retried 3x). */
  list(): Promise<KvListResponse>;
  /**
   * Batch-read até 64 keys em uma request. Keys ausentes no DB são omitidas
   * do map de retorno (não aparecem como `null`). Usado pelo template no
   * boot do AppRoot pra primear o cache — callers normais usam `get()`.
   * @throws SdkValidationError se qualquer key não bater o regex, ou se o
   *   array estiver vazio / >64 elementos.
   * @throws SdkError on network/5xx (retried 3x).
   */
  bulkRead(keys: string[]): Promise<BulkReadResponse>;
  /**
   * Cache em memória pras keys prefetched. O template chama
   * `cache.startPrefetch(keys, bulkRead)` 1× no boot; callers nunca tocam
   * isso diretamente — é exposto pro `PersistedKeysPrefetch` wiring.
   */
  cache: AppDataCache;
}

export function createAppData(
  client: HttpClient,
  appId: string,
  cache: AppDataCache,
): AppDataMethods {
  const base = `/api/app/${appId}/app-data`;
  return {
    cache,
    async get(key) {
      assertValidKey(key);
      // G50: consulta cache primed pelo bulkRead antes do network.
      const cached = await cache.get(key);
      if (cached !== undefined) return cached;
      // G49: backend novo devolve 200 { value: null, updated_at: null } direto.
      // try/catch aqui é shim de compat contra backend antigo (404). Remover em 0.7.0.
      try {
        return await client.request<KvResponse>('GET', `${base}/${encodeURIComponent(key)}`);
      } catch (err) {
        if (err instanceof SdkError && err.httpStatus === 404) return null;
        throw err;
      }
    },
    async set(key, value) {
      assertValidKey(key);
      const res = await client.request<KvResponse>('PUT', `${base}/${encodeURIComponent(key)}`, { body: { value } });
      cache.primeOne(key, res);
      broadcastAppDataChange(appId, key, value);
      return res;
    },
    async delete(key) {
      assertValidKey(key);
      await client.request<void>('DELETE', `${base}/${encodeURIComponent(key)}`);
      cache.invalidate(key);
      broadcastAppDataChange(appId, key, null);
    },
    list() {
      return client.request<KvListResponse>('GET', base);
    },
    async bulkRead(keys) {
      if (!Array.isArray(keys)) {
        throw new SdkValidationError('bulkRead requires an array of keys', 'app_data.invalid_keys', 400);
      }
      if (keys.length === 0) {
        throw new SdkValidationError('bulkRead requires at least 1 key', 'app_data.invalid_keys', 400);
      }
      if (keys.length > 64) {
        throw new SdkValidationError('bulkRead accepts at most 64 keys per call', 'app_data.invalid_keys', 400);
      }
      for (const k of keys) assertValidKey(k);
      return client.request<BulkReadResponse>('POST', `${base}:bulk-read`, { body: { keys } });
    },
  };
}
